import {MatchmakingPriority} from '../../../../shared/matchmaking/MatchmakingEnums'
import {MatchmakingMatchTypes} from '@colonist/game-base-types'
import {MatchmakingPreferences, MatchmakingRequest, MatchmakingStatusUpdate} from '../../../../shared/matchmaking/MatchmakingInterfaces'
import {SocketMatchmakingSend} from '../../../shared/scripts/socket/SocketMatchmakingSend'
import {ViewPopup} from '../../../shared/scripts/ui_popup/ViewPopup'
import {UIMatchmakingSelectMatchPopupView} from './UIMatchmakingSelectMatchPopupView'
import {UIMatchmakingSearchMatchPopupView} from './UIMatchmakingSearchMatchPopupView'
import {UIAjaxWrapper} from '../../../shared/scripts/network/UIAjaxWrapper'
import {UIAPIEndpoints} from '../../../shared/scripts/network/UIAPIEndpoints'
import {ELOTypeEnum, RankedConstants} from '../../../../shared/GameEnums'
import {RankedUserStateController} from '@colonist/leaderboard-types'
import {FindGameRankedState} from '../../../../shared/ranked/ranked_user_state_data/FindGameRankedState'
import {UIManager} from '../ui_html/UIManager'
import {TranslationObject} from '@colonist/i18n'
import {ClientUserState} from '../../../shared/scripts/ClientUserState'
import {ErrorsMatchmaking} from '../../../../shared/errors/ErrorsMatchmaking'
import {BaseError} from '../../../../shared/errors/BaseError'
import {setElementInnerHTML} from '../../../shared/scripts/ui_utils/DomUtils'
import {UIUtilsEjsVariables} from '../../../shared/scripts/UIUtilsEjsVariables'
import {Scene} from '../ui_html/UISceneTypes'
import {COLONIST_VERSION, ColonistMembershipType} from '@colonist/types'
import {UserSessionUtils} from '../../../shared/scripts/UserSessionUtils'
import {isCitiesAndKnightsMatchType, isRankedMatchType, userHasAccessToRankedCitiesAndKnights} from '../../../../shared/matchmaking/MatchmakingUtils'
import {ViewPopupLogin} from '../../../shared/scripts/ui_popup/ui_popup_membership_incentive/ViewPopupLogin'
import {UIDiscordInstance} from '../discord/child_iframe/UIDiscordInstance'
import {AnalyticEventValue} from '../../../../shared/entity_datas/EntityDatas'
import {ViewPopupBecomeMemberGameplay} from '../../../shared/scripts/ui_popup/ViewPopupBecomeMemberGameplay'
import {SDKRequest} from '../../../../shared/DiscordActivityTypes'
import {socket} from '../../../shared/scripts/EntryShared'
import {navigateToPage} from '../../../shared/scripts/ui_utils/ClientUtils.ts'

export class UIMatchmakingFindGameController {
    private readonly searchMatchPopupView: UIMatchmakingSearchMatchPopupView
    readonly selectMatchPopupView: UIMatchmakingSelectMatchPopupView
    private matchmakingErrorPopupView: ViewPopup | undefined
    private readonly uiManager: UIManager
    private dontShowSelectMatchPopup: boolean = false
    selectedMatchTypes: MatchmakingMatchTypes[] = []

    constructor(uiManager: UIManager) {
        this.uiManager = uiManager
        this.searchMatchPopupView = new UIMatchmakingSearchMatchPopupView()
        this.selectMatchPopupView = new UIMatchmakingSelectMatchPopupView()

        this.selectedMatchTypes = []
    }

    init() {
        socket.controller.registerSessionEstablishedObserver(() => {
            this.resendFindGame()
        })
    }

    private resendFindGame() {
        if(!this.searchMatchPopupView.isShowing()) return

        if(!SocketMatchmakingSend.lastMatchmakingRequest) {
            this.removePopups()
            return
        }

        SocketMatchmakingSend.sendFindGame()
    }

    updateMatchmakingStatus(matchmakingStatusUpdate: MatchmakingStatusUpdate) {
        this.selectMatchPopupView.hide()
        const primaryMatchType = this.selectedMatchTypes[0]
        this.searchMatchPopupView.update(matchmakingStatusUpdate, primaryMatchType)
    }

    removePopups() {
        this.searchMatchPopupView.hide()
        this.selectMatchPopupView.hide()
        if(this.matchmakingErrorPopupView != undefined) this.matchmakingErrorPopupView.removePopup()
    }

    loadMatchmakingState() {
        this.removePopups()
        void this.updateFindGameState()
    }

    showMatchmakingMatchTypeSelector() {
        this.loadMatchmakingState()

        this.selectMatchPopupView.setMatchmakingOptionsHeader()
        this.dontShowSelectMatchPopup ? this.dontShowSelectMatchPopup = false : this.selectMatchPopupView.show()

        this.setRankedOptionsDisplay()
    }

    sendFindGameMessageToServer(selectedMatchTypes: MatchmakingMatchTypes[], dontShowSelectMatchPopup: boolean = false) {
        if(this.showErrorIfMatchTypeSelectedAndUserNotEligible(selectedMatchTypes)) return

        // this.showSearchMatchPopupBeforeConnectingToServerForFasterResponsiveness()
        const adblockIsActive = this.uiManager.adController.adblockState.isAdblockActive
        const matchmakingRequest: MatchmakingRequest = {
            matchPreferences: [],
            adblockEnabled: adblockIsActive,
            clientVersion: COLONIST_VERSION,
        }

        for(const type of selectedMatchTypes) {
            const preference: MatchmakingPreferences = {
                matchType: type,
                priority: MatchmakingPriority.High,
            }
            matchmakingRequest.matchPreferences.push(preference)
        }
        this.selectedMatchTypes = selectedMatchTypes
        SocketMatchmakingSend.enterMatchmaker(matchmakingRequest)
        this.dontShowSelectMatchPopup = dontShowSelectMatchPopup
    }

    showMatchmakingErrorPopup(error: BaseError) {
        this.removePopups()
        const title: TranslationObject = error.title
        const errorMessage: TranslationObject = error.description
        const checkAction = () => undefined
        const xAction = () => undefined
        const canCancel = true
        this.matchmakingErrorPopupView = new ViewPopup(title, errorMessage, checkAction, xAction, canCancel)
        this.matchmakingErrorPopupView.show()
        this.matchmakingErrorPopupView.removeXButton()
    }

    private async updateFindGameState() {
        const displayError = false
        const response = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiFindGameState(), undefined, undefined, displayError)
        if(response == undefined) return
        const json = response.json as FindGameRankedState[]
        for(const gameType of json) {
            if(gameType.eloType == ELOTypeEnum.VisibleRanked1v1) {
                if(!gameType.active) this.selectMatchPopupView.removeOptionRanked1v1()
                if(gameType.rankedState != undefined) {
                    const rankedState = RankedUserStateController.deserialize(gameType.rankedState)
                    const statusText = rankedState.getFindGamePopupStatusText()
                    setElementInnerHTML(this.selectMatchPopupView.rankedStateRanked1v1, statusText)
                }
            }
            if(gameType.eloType == ELOTypeEnum.VisibleRanked4PClassic) {
                if(!gameType.active) this.selectMatchPopupView.removeOptionRankedBase()
                if(gameType.rankedState != undefined) {
                    const rankedState = RankedUserStateController.deserialize(gameType.rankedState)
                    const statusText = rankedState.getFindGamePopupStatusText()
                    setElementInnerHTML(this.selectMatchPopupView.rankedStateRankedBase, statusText)
                }
            }
            if(gameType.eloType == ELOTypeEnum.VisibleRankedCitiesAndKnights4P) {
                if(!gameType.active) this.selectMatchPopupView.removeOptionRankedCitiesAndKnights()
                if(gameType.rankedState != undefined) {
                    const rankedState = RankedUserStateController.deserialize(gameType.rankedState)
                    const statusText = rankedState.getFindGamePopupStatusText()
                    setElementInnerHTML(this.selectMatchPopupView.rankedStateCitiesAndKnights, statusText)
                }
            }
        }
    }

    private showErrorIfMatchTypeSelectedAndUserNotEligible(selectedMatchTypes: MatchmakingMatchTypes[]): boolean {
        const isRankedMatchTypeSelected = isRankedMatchType(selectedMatchTypes)
        if(!isRankedMatchTypeSelected) return false

        if(!ClientUserState.userState?.isLoggedIn) {
            new ViewPopupLogin()
            return true
        }

        const isCitiesAndKnightsMatchTypeSelected = isCitiesAndKnightsMatchType(selectedMatchTypes)
        const hasAccessToCitiesAndKnights = userHasAccessToRankedCitiesAndKnights(ClientUserState.userState?.items)
        if(isCitiesAndKnightsMatchTypeSelected && !hasAccessToCitiesAndKnights) {
            const checkAction = UIUtilsEjsVariables.isUserOnDiscord ? () => {
                void UIDiscordInstance.getDiscordInstance().sendSDKRequest(SDKRequest.PurchaseMembership, ColonistMembershipType.PremiumMonthly)
            } : () => {
                navigateToPage('/store#membership', true)
            }
            new ViewPopupBecomeMemberGameplay(checkAction, AnalyticEventValue.PopupMembershipIncentivesRankedCitiesAndKnights)
            return true
        }

        const isEligibleForRanked = UserSessionUtils.userEligibleForRanked()
        if(!isEligibleForRanked) {
            const requiredNumberOfGamesForRanked = RankedConstants.NumberOfGamesToUnlockRanked
            const rankedLockedError = new ErrorsMatchmaking.RankedLocked(ClientUserState.userState.totalCompletedGameCount, requiredNumberOfGamesForRanked)
            this.showMatchmakingErrorPopup(rankedLockedError)
            return true
        }

        return false
    }

    private setRankedOptionsDisplay() {
        if(this.shouldHideRankedOptions()) {
            this.hideRankedOptions()
            return
        }
        this.showRankedOptions()
    }

    private shouldHideRankedOptions(): boolean {
        const onLandingPage = this.uiManager.sceneController.scene == Scene.LandingPage
        const isUserOnMobile = UIUtilsEjsVariables.isUserOnMobile

        if(onLandingPage && !isUserOnMobile) return true

        return false
    }

    private hideRankedOptions() {
        const unrankedOptionsText = this.getUnrankedOptionsStatusText()

        this.selectMatchPopupView.hideRankedOptions()
        this.selectMatchPopupView.setUnrankedOptionsStatusText(unrankedOptionsText)
    }

    private showRankedOptions() {
        this.selectMatchPopupView.showRankedOptions()
        this.selectMatchPopupView.setUnrankedOptionsStatusText({key: 'strings:colonistCommon.utils.emptyString'})
    }

    // eslint-disable-next-line class-methods-use-this
    private getUnrankedOptionsStatusText(): TranslationObject {
        const emptyText: TranslationObject = {key: 'strings:colonistCommon.utils.emptyString'}
        const gamesToUnlockRankedText: TranslationObject = {
            key: 'strings:colonistCommon.rankedState.gamesToUnlockRanked',
            options: {
                gamesPlayedCount: ClientUserState.userState?.totalCompletedGameCount ?? 0,
                count: RankedConstants.NumberOfGamesToUnlockRanked,
            },
        }

        return UserSessionUtils.userEligibleForRanked() ? emptyText : gamesToUnlockRankedText
    }
}
